import type { WorkbenchMenu, WorkbenchMenuBarController, WorkbenchMenuItem } from "./workbench-menu-types.js";

const MOBILE_MENU_ID = "__actions__";

type WorkbenchMenuBarOptions = {
  getMenus(): WorkbenchMenu[];
  label?: string;
  mobileLabel?: string;
};

function itemRole(item: WorkbenchMenuItem): string {
  if (item.type === "checkbox") {
    return "menuitemcheckbox";
  }
  if (item.type === "radio") {
    return "menuitemradio";
  }
  return "menuitem";
}

function menusSignature(menus: WorkbenchMenu[]): string {
  return JSON.stringify(
    menus.map((menu) => ({
      id: menu.id,
      items: menu.items.map((item) => {
        if (item.type === "separator") {
          return { type: item.type };
        }
        return {
          checked: item.type === "checkbox" || item.type === "radio" ? item.checked : undefined,
          enabled: item.enabled,
          id: item.id,
          label: item.label,
          shortcut: item.shortcut,
          type: item.type
        };
      }),
      label: menu.label
    }))
  );
}

function triggerRoleButton(label: string): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "workbench-menu-bar__trigger";
  button.setAttribute("role", "menuitem");
  button.setAttribute("aria-haspopup", "menu");
  button.textContent = label;
  return button;
}

export function createWorkbenchMenuBar(host: HTMLElement, options: WorkbenchMenuBarOptions): WorkbenchMenuBarController {
  const doc = host.ownerDocument;
  const root = doc.createElement("div");
  root.className = "workbench-menu-shell";
  host.replaceChildren(root);
  root.addEventListener("keydown", (event) => {
    event.stopPropagation();
  });

  let openMenuId: string | null = null;
  let lastSignature = "";
  let renderedTriggers: HTMLButtonElement[] = [];
  let renderedMobileTrigger: HTMLButtonElement | null = null;
  let renderedMenuButtons: HTMLButtonElement[] = [];

  const currentMenus = (): WorkbenchMenu[] => options.getMenus();

  const currentMenu = (): WorkbenchMenu | null => {
    if (openMenuId === null || openMenuId === MOBILE_MENU_ID) {
      return null;
    }
    return currentMenus().find((menu) => menu.id === openMenuId) ?? null;
  };

  const enabledMenuButtons = (): HTMLButtonElement[] => renderedMenuButtons.filter((button) => !button.disabled);

  const focusTrigger = (menuId: string | null): void => {
    if (menuId === MOBILE_MENU_ID) {
      renderedMobileTrigger?.focus();
      return;
    }
    if (menuId === null) {
      return;
    }
    renderedTriggers.find((trigger) => trigger.dataset.menuId === menuId)?.focus();
  };

  const focusMenuButton = (index: number): void => {
    const buttons = enabledMenuButtons();
    if (buttons.length === 0) {
      focusTrigger(openMenuId);
      return;
    }
    const normalized = ((index % buttons.length) + buttons.length) % buttons.length;
    buttons[normalized]?.focus();
  };

  const closeMenu = (): void => {
    if (openMenuId === null) {
      return;
    }
    openMenuId = null;
    render(true);
  };

  const openMenu = (menuId: string, focus: "trigger" | "first" | "last" = "trigger"): void => {
    openMenuId = menuId;
    render(true);
    if (focus === "first") {
      focusMenuButton(0);
      return;
    }
    if (focus === "last") {
      const buttons = enabledMenuButtons();
      focusMenuButton(buttons.length - 1);
      return;
    }
    focusTrigger(menuId);
  };

  const switchMenu = (delta: number, focus: "trigger" | "first" | "last" = "first"): void => {
    const menus = currentMenus();
    if (menus.length === 0) {
      return;
    }
    const currentIndex = openMenuId === null || openMenuId === MOBILE_MENU_ID
      ? 0
      : Math.max(0, menus.findIndex((menu) => menu.id === openMenuId));
    const nextIndex = ((currentIndex + delta) % menus.length + menus.length) % menus.length;
    openMenu(menus[nextIndex]!.id, focus);
  };

  const onTriggerKeyDown = (event: KeyboardEvent, menuId: string, index: number): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openMenu(menuId, "first");
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      openMenu(menuId, "last");
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (openMenuId !== null && openMenuId !== MOBILE_MENU_ID) {
        switchMenu(1, "first");
      } else {
        renderedTriggers[(index + 1) % renderedTriggers.length]?.focus();
      }
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (openMenuId !== null && openMenuId !== MOBILE_MENU_ID) {
        switchMenu(-1, "first");
      } else {
        renderedTriggers[(index - 1 + renderedTriggers.length) % renderedTriggers.length]?.focus();
      }
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      renderedTriggers[0]?.focus();
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      renderedTriggers[renderedTriggers.length - 1]?.focus();
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
      focusTrigger(menuId);
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (openMenuId === menuId) {
        closeMenu();
      } else {
        openMenu(menuId, "first");
      }
    }
  };

  const onMenuItemKeyDown = (event: KeyboardEvent): void => {
    const buttons = enabledMenuButtons();
    const currentIndex = buttons.findIndex((button) => button === event.currentTarget);
    if (currentIndex < 0) {
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusMenuButton(currentIndex + 1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      focusMenuButton(currentIndex - 1);
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      focusMenuButton(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      focusMenuButton(buttons.length - 1);
      return;
    }
    if (event.key === "ArrowRight" && openMenuId !== null && openMenuId !== MOBILE_MENU_ID) {
      event.preventDefault();
      switchMenu(1, "first");
      return;
    }
    if (event.key === "ArrowLeft" && openMenuId !== null && openMenuId !== MOBILE_MENU_ID) {
      event.preventDefault();
      switchMenu(-1, "first");
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      const menuId = openMenuId;
      closeMenu();
      focusTrigger(menuId);
      return;
    }
    if (event.key === "Tab") {
      closeMenu();
    }
  };

  const executeItem = (item: WorkbenchMenuItem): void => {
    if (item.type === "separator" || !item.enabled) {
      return;
    }
    closeMenu();
    item.run();
  };

  const buildMenuOverlay = (menuId: string, menus: WorkbenchMenu[]): HTMLDivElement => {
    const overlay = doc.createElement("div");
    overlay.className = "workbench-menu-bar__overlay";
    overlay.dataset.openMenuId = menuId;
    overlay.setAttribute("role", "menu");
    overlay.tabIndex = -1;

    const appendItemButton = (item: WorkbenchMenuItem, menuLabel: string): void => {
      if (item.type === "separator") {
        const separator = doc.createElement("div");
        separator.className = "workbench-menu-bar__separator";
        separator.setAttribute("role", "separator");
        overlay.append(separator);
        return;
      }

      const button = doc.createElement("button");
      button.type = "button";
      button.className = "workbench-menu-bar__item";
      button.dataset.menuItemId = item.id;
      button.dataset.menuLabel = menuLabel;
      button.disabled = !item.enabled;
      button.setAttribute("role", itemRole(item));
      if (item.type === "checkbox" || item.type === "radio") {
        button.setAttribute("aria-checked", item.checked ? "true" : "false");
      }

      const indicator = doc.createElement("span");
      indicator.className = "workbench-menu-bar__item-indicator";
      indicator.textContent = item.type === "checkbox"
        ? item.checked ? "✓" : ""
        : item.type === "radio"
          ? item.checked ? "●" : ""
          : "";

      const label = doc.createElement("span");
      label.className = "workbench-menu-bar__item-label";
      label.textContent = item.label;

      const shortcut = doc.createElement("span");
      shortcut.className = "workbench-menu-bar__item-shortcut";
      shortcut.textContent = item.shortcut ?? "";
      shortcut.hidden = item.shortcut === undefined;

      button.append(indicator, label, shortcut);
      button.addEventListener("click", () => {
        executeItem(item);
      });
      button.addEventListener("keydown", onMenuItemKeyDown);
      renderedMenuButtons.push(button);
      overlay.append(button);
    };

    if (menuId === MOBILE_MENU_ID) {
      overlay.dataset.mobile = "true";
      overlay.setAttribute("aria-label", options.mobileLabel ?? "Actions menu");
      for (const menu of menus) {
        const heading = doc.createElement("div");
        heading.className = "workbench-menu-bar__section-label";
        heading.textContent = menu.label;
        overlay.append(heading);
        menu.items.forEach((item) => {
          appendItemButton(item, menu.label);
        });
      }
      return overlay;
    }

    const menu = menus.find((entry) => entry.id === menuId);
    if (menu === undefined) {
      return overlay;
    }

    overlay.setAttribute("aria-label", `${menu.label} menu`);
    menu.items.forEach((item) => {
      appendItemButton(item, menu.label);
    });
    return overlay;
  };

  const positionOverlay = (overlay: HTMLDivElement): void => {
    if (openMenuId === MOBILE_MENU_ID) {
      return;
    }
    const trigger = renderedTriggers.find((button) => button.dataset.menuId === openMenuId);
    if (trigger === undefined) {
      return;
    }
    const overlayWidth = overlay.getBoundingClientRect().width;
    const maxLeft = Math.max(0, root.clientWidth - overlayWidth);
    const left = Math.max(0, Math.min(trigger.offsetLeft, maxLeft));
    overlay.style.left = `${left}px`;
  };

  const render = (force = false): void => {
    const menus = currentMenus();
    if (openMenuId !== null && openMenuId !== MOBILE_MENU_ID && !menus.some((menu) => menu.id === openMenuId)) {
      openMenuId = null;
    }
    const signature = menusSignature(menus);
    if (!force && signature === lastSignature) {
      return;
    }

    renderedTriggers = [];
    renderedMobileTrigger = null;
    renderedMenuButtons = [];
    root.replaceChildren();

    const menubar = doc.createElement("div");
    menubar.className = "workbench-menu-bar";
    menubar.setAttribute("role", "menubar");
    menubar.setAttribute("aria-label", options.label ?? "Editor menus");

    menus.forEach((menu, index) => {
      const trigger = triggerRoleButton(menu.label);
      trigger.dataset.menuId = menu.id;
      trigger.setAttribute("aria-expanded", openMenuId === menu.id ? "true" : "false");
      trigger.addEventListener("click", () => {
        if (openMenuId === menu.id) {
          closeMenu();
          trigger.focus();
          return;
        }
        openMenu(menu.id);
      });
      trigger.addEventListener("keydown", (event) => {
        onTriggerKeyDown(event, menu.id, index);
      });
      renderedTriggers.push(trigger);
      menubar.append(trigger);
    });

    const mobileTrigger = doc.createElement("button");
    mobileTrigger.type = "button";
    mobileTrigger.className = "workbench-menu-bar__mobile-trigger";
    mobileTrigger.textContent = options.mobileLabel ?? "Actions";
    mobileTrigger.setAttribute("aria-haspopup", "menu");
    mobileTrigger.setAttribute("aria-expanded", openMenuId === MOBILE_MENU_ID ? "true" : "false");
    mobileTrigger.addEventListener("click", () => {
      if (openMenuId === MOBILE_MENU_ID) {
        closeMenu();
        mobileTrigger.focus();
        return;
      }
      openMenu(MOBILE_MENU_ID, "first");
    });
    mobileTrigger.addEventListener("keydown", (event) => {
      if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openMenu(MOBILE_MENU_ID, "first");
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu();
      }
    });
    renderedMobileTrigger = mobileTrigger;

    root.append(menubar, mobileTrigger);

    if (openMenuId !== null) {
      const overlay = buildMenuOverlay(openMenuId, menus);
      root.append(overlay);
      positionOverlay(overlay);
    }
    lastSignature = signature;
  };

  doc.addEventListener("pointerdown", (event) => {
    if (openMenuId === null) {
      return;
    }
    if (!(event.target instanceof Node)) {
      closeMenu();
      return;
    }
    if (root.contains(event.target)) {
      return;
    }
    closeMenu();
  });

  render(true);

  return {
    close() {
      closeMenu();
    },
    refresh() {
      render();
    }
  };
}
